import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard } from './auth.guard';
import { LayoutComponent } from './layout/layout.component';
import { LoaderComponent } from './layout/loader/loader.component';
import { AuthModule } from './theme/auth/auth.module';
import { DashboardComponent } from './theme/dashboard/dashboard.component';
import { JournalComponent } from './theme/journal/journal.component';
import { MyOrganizationComponent } from './theme/my-organization/my-organization.component';
import { AddEditOrganizationComponent } from './theme/add-organization/add-edit-organization.component';
import { MyProfileComponent } from './theme/my-profile/my-profile.component';
import { MySitesComponent } from './theme/my-sites/my-sites.component';
import { PrintComponent } from './theme/print/print.component';
import { MyLogsComponent } from './theme/my-logs/my-logs.component';
import { AdminModule } from './theme/admin/admin.module';
import { TitleResolver } from './resolver/title.resolver';
import { AddSiteComponent } from './theme/add-site/add-site.component';
import { AdminClustersResolver } from './resolver/admins-clusters-resolver.service';
import { AddEditTlsOptionsComponent } from './theme/my-organization/tls-options/add-edit-tls-options/add-edit-tls-options.component';
import {
  ADD_ORGANIZATION_ROUTE,
  ADD_ROUTE,
  ADMIN_CLUSTER_DASHBOARD_ROUTE,
  ADMIN_CLUSTER_DELEGATION_ROUTE,
  ADMIN_CLUSTER_IA_PARAMETERS_ROUTE,
  ADMIN_CLUSTER_JOURNAL_ROUTE,
  ADMIN_CLUSTER_LOGS_ROUTE,
  ADMIN_CLUSTER_MAINTENANCE_ROUTE,
  ADMIN_CLUSTER_METRICS_ROUTE,
  ADMIN_CLUSTER_SITES_ROUTE,
  ADMIN_PREFIX,
  API_DOC_ROUTE,
  AUTH_LOGIN_ROUTE,
  AUTH_PREFIX,
  DASHBOARD_ROUTE,
  JOURNAL_ROUTE,
  MFA_VALIDATION,
  MY_LOGS_ROUTE,
  MY_ORGANIZATION_CUSTOM_PAGES_PROFILES_ROUTE,
  MY_ORGANIZATION_ROUTE,
  MY_ORGANIZATION_TLS_OPTIONS_ROUTE,
  MY_PROFILE_ROUTE,
  MY_SITES_ROUTE,
  PRINT_ROUTE,
} from './app-routing.constants';
import { AddCustomPagesProfile } from './theme/my-organization/custom-pages-profiles/add-custom-pages-profile/add-custom-pages-profile';
import { unsavedChangesGuard } from '@shared/guards/GlobalUnsavedChangesGuard';
import { MfaValidationComponent } from '@shared/components/mfa-validation/mfa-validation.component';
import { MfaOrganizationGuard } from '@shared/guards/MfaOrganizationGuard';
import { AdminClusterGuard } from '@shared/guards/AdminClusterGuard';

const routes: Routes = [
  {
    path: '',
    component: LoaderComponent,
    children: [
      {
        path: '',
        redirectTo: AUTH_LOGIN_ROUTE,
        pathMatch: 'full',
        canLoad: [AuthGuard],
        title: TitleResolver,
        data: { title: 'Login' },
      },
      {
        path: AUTH_PREFIX,
        loadChildren: () => AuthModule,
      },
    ],
  },
  {
    path: '',
    component: LayoutComponent,
    canLoad: [AuthGuard],
    title: TitleResolver,
    children: [
      {
        path: ADMIN_PREFIX,
        loadChildren: () => AdminModule,
      },
      {
        path: MY_PROFILE_ROUTE,
        component: MyProfileComponent,
        canActivate: [AuthGuard],
        title: TitleResolver,
        data: { title: 'MyProfile' },
      },
      {
        path: MFA_VALIDATION,
        component: MfaValidationComponent,
        canActivate: [AuthGuard, MfaOrganizationGuard],
        title: TitleResolver,
        data: { title: 'Mfa' },
      },
      {
        path: MY_ORGANIZATION_ROUTE,
        component: MyOrganizationComponent,
        canActivate: [AuthGuard, MfaOrganizationGuard],
        title: TitleResolver,
        data: { title: 'MyOrganization' },
      },
      {
        path: MY_ORGANIZATION_TLS_OPTIONS_ROUTE + '/' + ADD_ROUTE,
        component: AddEditTlsOptionsComponent,
        canActivate: [AuthGuard, MfaOrganizationGuard],
        title: TitleResolver,
        data: { title: 'Options TLS' },
      },
      {
        path: MY_ORGANIZATION_TLS_OPTIONS_ROUTE + '/:uid',
        component: AddEditTlsOptionsComponent,
        canActivate: [AuthGuard, MfaOrganizationGuard],
        title: TitleResolver,
        data: { title: 'Options TLS' },
      },
      {
        path: MY_ORGANIZATION_CUSTOM_PAGES_PROFILES_ROUTE + '/' + ADD_ROUTE,
        component: AddCustomPagesProfile,
        canActivate: [AuthGuard, MfaOrganizationGuard],
        title: TitleResolver,
        data: { title: 'Personnalisation des erreurs' },
        canDeactivate: [unsavedChangesGuard],
      },
      {
        path: MY_ORGANIZATION_CUSTOM_PAGES_PROFILES_ROUTE + '/:uid',
        component: AddCustomPagesProfile,
        canActivate: [AuthGuard, MfaOrganizationGuard],
        title: TitleResolver,
        data: { title: 'Personnalisation des erreurs' },
        canDeactivate: [unsavedChangesGuard],
      },
      {
        path: ADD_ORGANIZATION_ROUTE,
        component: AddEditOrganizationComponent,
        canActivate: [AuthGuard, MfaOrganizationGuard],
      },
      {
        path: JOURNAL_ROUTE,
        component: JournalComponent,
        canActivate: [AuthGuard, MfaOrganizationGuard],
        title: TitleResolver,
        data: { title: 'Journal' },
      },
      {
        path: DASHBOARD_ROUTE,
        component: DashboardComponent,
        canActivate: [AuthGuard, MfaOrganizationGuard],
        title: TitleResolver,
        data: { title: 'TableauDeBord' },
      },
      {
        path: PRINT_ROUTE,
        component: PrintComponent,
        canActivate: [AuthGuard, MfaOrganizationGuard],
      },
      {
        path: MY_LOGS_ROUTE,
        canDeactivate: [unsavedChangesGuard],

        component: MyLogsComponent,
        canActivate: [AuthGuard, MfaOrganizationGuard],
        title: TitleResolver,
        data: { title: 'Tracker' },
      },
      {
        path: MY_SITES_ROUTE,
        canDeactivate: [unsavedChangesGuard],
        component: MySitesComponent,
        data: { title: 'MySites', adminClusterView: false },
        canActivate: [AuthGuard, MfaOrganizationGuard],
        title: TitleResolver,
      },
      {
        path: MY_SITES_ROUTE + '/add',
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () => import('./theme/add-site/add-site.component').then((m) => m.AddSiteComponent),
        canActivate: [AuthGuard, MfaOrganizationGuard],
        title: TitleResolver,
        data: { title: 'MySites', adminClusterView: false },
      },
      {
        path: MY_SITES_ROUTE + '/:siteName',
        canDeactivate: [unsavedChangesGuard],
        component: AddSiteComponent,
        canActivate: [AuthGuard, MfaOrganizationGuard],
        title: TitleResolver,
        data: { title: 'PageTitle.SiteConfig', adminClusterView: false },
      },
      {
        path: API_DOC_ROUTE,
        loadComponent: () => import('./theme/api/v1/api-doc.component').then((m) => m.ApiDocComponent),
        canActivate: [AuthGuard, MfaOrganizationGuard],
        title: TitleResolver,
        data: { title: 'API Doc v1' },
      },
      {
        path: API_DOC_ROUTE + '/v1',
        loadComponent: () => import('./theme/api/v1/api-doc.component').then((m) => m.ApiDocComponent),
        canActivate: [AuthGuard, MfaOrganizationGuard],
        title: TitleResolver,
        data: { title: 'API Doc v1' },
      },
      {
        path: API_DOC_ROUTE + '/v2',
        loadComponent: () => import('./theme/api/swagger-ui/swagger-ui.component').then((m) => m.SwaggerUIComponent),
        canActivate: [AuthGuard, MfaOrganizationGuard],
        title: TitleResolver,
        data: { title: 'API Doc v2' },
      },
      {
        path: API_DOC_ROUTE + '/terraform',
        loadComponent: () => import('./theme/api/terraform/terraform.component').then((m) => m.TerraformComponent),
        canActivate: [AuthGuard, MfaOrganizationGuard],
        title: TitleResolver,
        data: { title: 'Terraform' },
      },
      {
        path: ADMIN_CLUSTER_DASHBOARD_ROUTE,
        component: DashboardComponent,
        data: { title: 'TableauDeBord', adminClusterView: true },
        canActivate: [AuthGuard, MfaOrganizationGuard, AdminClusterGuard],
        title: TitleResolver,
      },
      {
        path: ADMIN_CLUSTER_LOGS_ROUTE,
        component: MyLogsComponent,
        data: { title: 'Logs', adminClusterView: true },
        canActivate: [AuthGuard, MfaOrganizationGuard, AdminClusterGuard],
        title: TitleResolver,
      },
      {
        path: ADMIN_CLUSTER_SITES_ROUTE,
        component: MySitesComponent,
        data: { title: 'Sites', adminClusterView: true },
        resolve: { clusters: AdminClustersResolver },
        canActivate: [AuthGuard, MfaOrganizationGuard, AdminClusterGuard],
        title: TitleResolver,
      },
      {
        path: ADMIN_CLUSTER_SITES_ROUTE + '/:siteName',
        loadComponent: () => import('./theme/add-site/add-site.component').then((m) => m.AddSiteComponent),
        canActivate: [AuthGuard, MfaOrganizationGuard, AdminClusterGuard],
        title: TitleResolver,
        data: { title: 'MySites', adminClusterView: true },
      },
      {
        path: ADMIN_CLUSTER_METRICS_ROUTE,
        loadComponent: () => import('./theme/admin-cluster/metrics/metrics.component').then((m) => m.MetricsComponent),
        resolve: { clusters: AdminClustersResolver },
        canActivate: [AuthGuard, MfaOrganizationGuard, AdminClusterGuard],
        title: TitleResolver,
        data: { title: 'Metrics' },
      },
      {
        path: ADMIN_CLUSTER_JOURNAL_ROUTE,
        loadComponent: () =>
          import('./theme/admin-cluster/journal-admin-cluster/journal-admin-cluster.component').then(
            (m) => m.JournalAdminClusterComponent,
          ),
        resolve: { clusters: AdminClustersResolver },
        canActivate: [AuthGuard, MfaOrganizationGuard, AdminClusterGuard],
        title: TitleResolver,
        data: { title: 'Activities' },
      },
      {
        path: ADMIN_CLUSTER_DELEGATION_ROUTE,
        loadComponent: () =>
          import('./theme/admin-cluster/delegation/delegation.component').then((m) => m.DelegationComponent),
        resolve: { clusters: AdminClustersResolver },
        canActivate: [AuthGuard, MfaOrganizationGuard, AdminClusterGuard],
        title: TitleResolver,
        data: { title: 'Delegation' },
      },
      {
        path: ADMIN_CLUSTER_IA_PARAMETERS_ROUTE,
        loadComponent: () =>
          import('./theme/admin-cluster/ai-parameters/ai-parameters.component').then((m) => m.AiParametersComponent),
        resolve: { clusters: AdminClustersResolver },
        canActivate: [AuthGuard, MfaOrganizationGuard, AdminClusterGuard],
        title: TitleResolver,
        data: { title: 'AIParameters' },
      },
      {
        path: ADMIN_CLUSTER_MAINTENANCE_ROUTE,
        loadComponent: () =>
          import('./theme/admin-cluster/maintenance/maintenance.component').then((m) => m.MaintenanceComponent),
        resolve: { clusters: AdminClustersResolver },
        canActivate: [AuthGuard, MfaOrganizationGuard, AdminClusterGuard],
        title: TitleResolver,
        data: { title: 'Maintenance' },
      },
      { path: '**', redirectTo: DASHBOARD_ROUTE, pathMatch: 'full' },
    ],
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      useHash: true,
      bindToComponentInputs: true,
      paramsInheritanceStrategy: 'always',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
